import { Outlet, useNavigate } from "react-router-dom";
import { lazy, Suspense, useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { SidebarProvider } from "@/components/ui/sidebar";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { LogOut, Search } from "lucide-react";
import { useAuthSession } from "@/hooks/useAuthSession";
import { DynamicBreadcrumb } from "@/features/shared/DynamicBreadcrumb";
import { authKeys, bankKeys, dashboardKeys } from "@/data/query-keys";
import { useToast } from "@/hooks/use-toast";
import { runOnboardingDataCatchUpSyncAsync } from "@/lib/onboardingDataSync";
import { prefetchCommonDataImmediate } from "@/lib/prefetchUtils";
import { setActAsCompanyId } from "@/lib/actAsCompany";
import { DollarBillIcon } from "@/components/DollarBillIcon";
import { SEOHead } from "@/components/seo/SEOHead";

// Lazy load heavy components (bundle-dynamic-imports)
const AppSidebar = lazy(() => import("@/features/shared/AppSidebar").then(m => ({ default: m.AppSidebar })));
const GlobalAdminSidebar = lazy(() => import("@/features/shared/GlobalAdminSidebar").then(m => ({ default: m.GlobalAdminSidebar })));
const ChatAssistant = lazy(() => import("@/features/shared/ChatAssistant").then(m => ({ default: m.ChatAssistant })));
const CommandPalette = lazy(() => import("@/features/shared/CommandPalette").then(m => ({ default: m.CommandPalette })));
const KeyboardShortcutsHelp = lazy(() => import("@/features/shared/KeyboardShortcutsHelp").then(m => ({ default: m.KeyboardShortcutsHelp })));

// Banner when global admin is "acting as" a company
function ActAsBanner({
  companyName,
  onExit,
}: {
  companyName: string;
  onExit: () => void;
}) {
  return (
    <div className="bg-amber-500/15 border-b border-amber-500/30 px-4 py-2 flex items-center justify-between gap-2 text-sm">
      <span className="text-amber-800 dark:text-amber-200 font-medium">
        Viewing as: <strong>{companyName}</strong>
      </span>
      <Button variant="outline" size="sm" onClick={onExit} className="shrink-0 border-amber-600/50 hover:bg-amber-500/20">
        Exit act-as
      </Button>
    </div>
  );
}

// Minimal sidebar skeleton for suspense fallback
const SidebarSkeleton = () => (
  <div className="w-64 border-r bg-card p-4 min-h-screen">
    <Skeleton className="h-8 w-32 mb-6" />
    <div className="space-y-3">
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-3/4" />
      <Skeleton className="h-8 w-full" />
    </div>
  </div>
);

const Index = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [commandOpen, setCommandOpen] = useState(false);
  
  // Use auth session hook - eliminates waterfall
  const { data: session, isLoading } = useAuthSession();
  
  // Derived values from session (rerender-derived-state-no-effect)
  const userEmail = session?.user?.email || "";
  const companyId = session?.effectiveCompanyId ?? session?.profile?.company_id ?? undefined;
  const companyName = session?.effectiveCompanyName ?? session?.company?.name ?? "";
  const isGlobalAdmin = session?.isGlobalAdmin ?? false;
  const isActingAsCompany = Boolean(session?.effectiveCompanyId);
  const showAdminUI = isGlobalAdmin && !isActingAsCompany;

  // Prefetch common data and catch-up sync when session is ready
  useEffect(() => {
    if (!companyId) return;
    prefetchCommonDataImmediate(queryClient, companyId);
    // Catch-up: sync onboarding mapped data, then show toast and refresh dashboard
    runOnboardingDataCatchUpSyncAsync(companyId).then((res) => {
      if (res.failed.length > 0) {
        toast({
          variant: "destructive",
          title: "Sync had issues",
          description: res.failed.map((f) => `${f.module}: ${f.error}`).join(" • "),
        });
      }
      if (res.synced.length > 0) {
        toast({
          title: "Dashboard data synced",
          description: "Your onboarding data is now reflected on the dashboard.",
        });
        queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
        queryClient.invalidateQueries({ queryKey: bankKeys.all });
      }
    }).catch((err) => console.error("[runOnboardingDataCatchUpSync]", err));
  }, [companyId, queryClient, toast]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  // Loading state - full page skeleton
  if (isLoading) {
    return (
      <SidebarProvider>
        <div className="min-h-screen flex w-full bg-background">
          <SidebarSkeleton />
          <div className="flex-1 flex flex-col">
            <header className="h-14 flex items-center justify-between border-b px-4 bg-card">
              <div className="flex items-center gap-3">
                <Skeleton className="h-7 w-7 rounded" />
                <Skeleton className="h-5 w-24" />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-8 w-20" />
                <Skeleton className="h-8 w-20" />
              </div>
            </header>
            <main className="flex-1 p-6">
              <Skeleton className="h-8 w-64 mb-4" />
              <Skeleton className="h-4 w-48" />
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <SEOHead
        title="Dashboard"
        description="Dollar Bill dashboard"
        noIndex
      />
      <div className="min-h-screen flex w-full bg-background">
        {/* Lazy-loaded sidebar with suspense */}
        <Suspense fallback={<SidebarSkeleton />}>
          {showAdminUI ? <GlobalAdminSidebar /> : <AppSidebar />}
        </Suspense>
        <div className="flex-1 flex flex-col">
          {/* Act-as banner: global admin viewing as another company */}
          {isActingAsCompany && (
            <ActAsBanner
              companyName={companyName}
              onExit={() => {
                setActAsCompanyId(null);
                queryClient.invalidateQueries({ queryKey: authKeys.session() });
              }}
            />
          )}
          {/* Header is eagerly loaded for fast first paint */}
          <header className="h-14 flex items-center justify-between border-b px-4 bg-card">
            <div className="flex items-center gap-3">
              <DollarBillIcon size={28} />
              <span className="font-semibold"><span className="text-foreground">Dollar</span>{" "}<span className="text-vault-green">Bill</span></span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCommandOpen(true)}
                className="gap-2 text-muted-foreground"
              >
                <Search className="h-4 w-4" />
                <span className="hidden sm:inline">Search</span>
                <kbd className="hidden sm:inline pointer-events-none h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100">
                  <span className="text-xs">⌘</span>K
                </kbd>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSignOut}
                className="gap-2"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Sign Out</span>
              </Button>
            </div>
          </header>
          <DynamicBreadcrumb />
          {/* Outlet is eagerly loaded for fast route rendering */}
          <main className="flex-1">
            <Outlet />
          </main>
        </div>
      </div>
      {/* Lazy-loaded chat, command palette, keyboard shortcuts */}
      <Suspense fallback={null}>
        <ChatAssistant />
      </Suspense>
      <Suspense fallback={null}>
        <CommandPalette open={commandOpen} setOpen={setCommandOpen} />
      </Suspense>
      <Suspense fallback={null}>
        <KeyboardShortcutsHelp />
      </Suspense>
    </SidebarProvider>
  );
};

export default Index;
